import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  tap,
  catchError,
  defer,
  finalize,
  firstValueFrom,
  from,
  of,
  shareReplay,
  switchMap,
  timer,
  throwError
} from 'rxjs';
import { Router } from '@angular/router';
import { AUTH_API_URL } from '../constants/app.constants';
import {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
  LogoutRequest,
  UserProfile,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ResetPasswordValidationResponse,
  ResetPasswordResponse,
  VerifyTokenRequest,
  VerifyTokenResponse
} from '../models/auth.model';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { decodeJwt, isTokenExpired, shouldRefreshToken } from '../utils/jwt.util';
import { WebPushService } from './web-push.service';
import {
  ReloginDialogComponent,
  ReloginDialogData
} from '../shared/components/relogin-dialog/relogin-dialog.component';

class NoRefreshTokenError extends Error {
  constructor() {
    super('No refresh token available');
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private webPushService = inject(WebPushService);
  private injector = inject(Injector);

  // Hộp thoại đăng nhập lại tại chỗ: dùng chung cho mọi request 401 đang chờ.
  private reauthPromise?: Promise<boolean>;
  private reauthResolve?: (success: boolean) => void;
  private reauthModalRef?: NzModalRef<ReloginDialogComponent, boolean>;

  private currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  // Timer for periodic token refresh
  private refreshTimer?: ReturnType<typeof setInterval>;
  private refreshInFlight$?: Observable<RefreshResponse>;
  private authChannel?: BroadcastChannel;
  private readonly REFRESH_CHECK_INTERVAL = 60 * 1000; // Check every 1 minute
  private readonly REFRESH_LOCK_NAME = 'tnd-auth-token-refresh';
  private readonly AUTH_CHANNEL_NAME = 'tnd-auth-session';

  constructor() {
    this.initializeCrossTabSync();

    // Load user from storage on init
    // Don't clear auth on error - let the app handle it naturally
    // If token is invalid, user will be redirected by guards or when making actual API calls
    const token = this.getAccessToken();
    if (token) {
      // Start token refresh timer
      this.startTokenRefreshTimer();
      
      this.getCurrentUser().subscribe({
        next: (user) => {
          this.currentUserSubject.next(user);
        },
        error: (error) => {
          // Don't clear auth in constructor - just log the error
          // The app will handle invalid tokens when making actual API calls
          // This prevents logout on F5 when there are temporary network issues
          console.warn('[AuthService] Failed to load user on init:', error);
          // Keep token - let guards or API calls handle authentication
        }
      });
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    });
  }

  // Token Management
  getAccessToken(): string | null {
    const localToken = localStorage.getItem('access_token');
    const sessionToken = sessionStorage.getItem('access_token');
    const token = localToken || sessionToken;
    
    // Removed verbose logging - this method is called too frequently
    // Uncomment for debugging if needed:
    // console.log('[AuthService] getAccessToken:', {
    //   hasLocalToken: !!localToken,
    //   hasSessionToken: !!sessionToken,
    //   tokenLength: token?.length,
    //   tokenPreview: token ? token.substring(0, 20) + '...' : null
    // });
    
    return token;
  }

  getRefreshToken(): string | null {
    const localToken = localStorage.getItem('refresh_token');
    const sessionToken = sessionStorage.getItem('refresh_token');
    const token = localToken || sessionToken;
    
    // Removed verbose logging - this method is called too frequently
    // Uncomment for debugging if needed:
    // console.log('[AuthService] getRefreshToken:', {
    //   hasLocalToken: !!localToken,
    //   hasSessionToken: !!sessionToken,
    //   tokenLength: token?.length
    // });
    
    return token;
  }

  setTokens(accessToken: string, refreshToken: string, rememberMe: boolean = false): void {
    const storage = rememberMe ? localStorage : sessionStorage;

    // Tránh token cũ ở storage còn lại được ưu tiên khi người dùng đổi rememberMe.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    storage.setItem('access_token', accessToken);
    storage.setItem('refresh_token', refreshToken);
    
    // Restart timer after setting new tokens
    this.startTokenRefreshTimer();
  }

  clearAuth(): void {
    // Stop timer before clearing auth
    this.stopTokenRefreshTimer();
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    this.currentUserSubject.next(null);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Check if access token is expired
   */
  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    return !token || isTokenExpired(token);
  }

  /**
   * Check if token should be refreshed (at 2/3 of lifetime)
   */
  shouldRefreshAccessToken(): boolean {
    const token = this.getAccessToken();
    return !!token && shouldRefreshToken(token);
  }

  /**
   * Start periodic token refresh timer
   * Checks every minute if token needs refresh (at 2/3 lifetime)
   */
  private startTokenRefreshTimer(): void {
    // Clear existing timer if any
    this.stopTokenRefreshTimer();

    this.refreshTimer = setInterval(() => {
      const token = this.getAccessToken();
      if (!token) {
        this.stopTokenRefreshTimer();
        return;
      }

      // Check if token needs refresh (at 2/3 lifetime)
      if (isTokenExpired(token) || shouldRefreshToken(token)) {
        this.refreshToken().subscribe({
          error: (error) => {
            console.error('[AuthService] Timer: Refresh failed', error);
            // Giữ timer để thử lại nếu đây chỉ là lỗi mạng/backend tạm thời.
          }
        });
      }
    }, this.REFRESH_CHECK_INTERVAL);
  }

  /**
   * Stop periodic token refresh timer
   */
  private stopTokenRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  /**
   * Automatically refresh token if needed (at 2/3 of lifetime or expired)
   * Returns Observable that completes when token is refreshed or if no refresh is needed
   */
  refreshTokenIfNeeded(): Observable<RefreshResponse | null> {
    const token = this.getAccessToken();

    // No token, nothing to refresh
    if (!token) {
      return of(null);
    }

    // Token is expired or should be refreshed (at 2/3 lifetime)
    if (isTokenExpired(token) || shouldRefreshToken(token)) {
      return this.refreshToken().pipe(
        catchError((error) => {
          console.error('[AuthService] refreshTokenIfNeeded: Refresh failed', error);
          return throwError(() => error);
        })
      );
    }

    return of(null);
  }

  // API Methods
  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${AUTH_API_URL}/register`, data);
  }

  login(data: LoginRequest, includeScoped: boolean = true, rememberMe: boolean = false): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${AUTH_API_URL}/login?include_scoped=${includeScoped}`,
      data
    ).pipe(
      tap((response) => {
        this.setTokens(response.access_token, response.refresh_token, rememberMe);
        // Get user profile after login
        this.getCurrentUser().subscribe({
          next: (user) => {
            this.currentUserSubject.next(user);
          }
        });
      })
    );
  }

  refreshToken(includeScoped: boolean = true): Observable<RefreshResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.executeRefresh(includeScoped).pipe(
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshInFlight$;
  }

  /**
   * Thực hiện refresh token tuần tự giữa các tab nếu trình duyệt hỗ trợ Web Locks.
   * @param includeScoped Có yêu cầu backend trả scoped permissions hay không.
   * @returns Kết quả refresh token.
   */
  private executeRefresh(includeScoped: boolean): Observable<RefreshResponse> {
    const accessTokenBeforeRefresh = this.getAccessToken();

    return defer((): Observable<RefreshResponse> => {
      const refreshTask = () => this.requestNewTokens(includeScoped, accessTokenBeforeRefresh);

      if (typeof navigator !== 'undefined' && navigator.locks) {
        const lockedRefresh = new Promise<RefreshResponse>((resolve, reject) => {
          navigator.locks.request(this.REFRESH_LOCK_NAME, refreshTask).then(resolve, reject);
        });
        return from(lockedRefresh);
      }

      return from(refreshTask());
    }).pipe(
      catchError((error: unknown) => {
        if (this.isRefreshTokenRejected(error) || error instanceof NoRefreshTokenError) {
          // Tab khác có thể vừa rotate token; chờ message đồng bộ trước khi kết thúc phiên.
          return timer(150).pipe(
            switchMap(() => {
              const latestAccessToken = this.getAccessToken();
              if (latestAccessToken && latestAccessToken !== accessTokenBeforeRefresh) {
                return of(this.currentTokensAsResponse());
              }

              // Ngoài lock refresh: hộp thoại có thể mở lâu, không được giữ lock của các tab khác.
              return from(this.recoverExpiredSession(error));
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Gọi API refresh hoặc dùng token mới nếu tab khác vừa refresh thành công.
   * @param includeScoped Có yêu cầu backend trả scoped permissions hay không.
   * @param accessTokenBeforeRefresh Access token tại thời điểm bắt đầu chờ refresh.
   * @returns Kết quả chứa access token đang có hiệu lực.
   */
  private async requestNewTokens(
    includeScoped: boolean,
    accessTokenBeforeRefresh: string | null
  ): Promise<RefreshResponse> {
    const currentAccessToken = this.getAccessToken();
    const currentRefreshToken = this.getRefreshToken();

    if (currentAccessToken && currentAccessToken !== accessTokenBeforeRefresh) {
      return {
        access_token: currentAccessToken,
        refresh_token: currentRefreshToken || '',
        token_type: 'bearer'
      };
    }

    if (!currentRefreshToken) {
      throw new NoRefreshTokenError();
    }

    const rememberMe = !!localStorage.getItem('access_token');
    const response = await firstValueFrom(
      this.http.post<RefreshResponse>(
        `${AUTH_API_URL}/refresh?include_scoped=${includeScoped}`,
        { refresh_token: currentRefreshToken } as RefreshRequest
      )
    );

    this.setTokens(
      response.access_token,
      response.refresh_token || currentRefreshToken,
      rememberMe
    );
    this.authChannel?.postMessage({
      type: 'tokens-refreshed',
      accessToken: response.access_token,
      refreshToken: response.refresh_token || currentRefreshToken,
      rememberMe
    });

    return response;
  }

  private initializeCrossTabSync(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    this.authChannel = new BroadcastChannel(this.AUTH_CHANNEL_NAME);
    this.authChannel.onmessage = (event: MessageEvent) => {
      const data = event.data;
      if (
        data?.type !== 'tokens-refreshed'
        || typeof data?.accessToken !== 'string'
        || typeof data?.refreshToken !== 'string'
      ) {
        return;
      }

      this.setTokens(data.accessToken, data.refreshToken, !!data.rememberMe);
      // Tab khác vừa đăng nhập lại / refresh thành công: tab này không cần hỏi mật khẩu nữa.
      this.finishReauthentication(true);
    };
  }

  private currentTokensAsResponse(): RefreshResponse {
    return {
      access_token: this.getAccessToken() || '',
      refresh_token: this.getRefreshToken() || '',
      token_type: 'bearer'
    };
  }

  /**
   * Phiên không thể refresh: hỏi người dùng đăng nhập lại ngay trên trang hiện tại để không mất
   * dữ liệu đang nhập. Chỉ khi người dùng chọn đăng xuất mới quay về trang /login.
   */
  private async recoverExpiredSession(error: unknown): Promise<RefreshResponse> {
    if (await this.requestReauthentication()) {
      return this.currentTokensAsResponse();
    }

    this.endExpiredSession();
    throw error;
  }

  /**
   * Mở hộp thoại đăng nhập lại (một hộp thoại dùng chung cho mọi request đang chờ).
   * @returns true nếu đã có phiên mới, false nếu người dùng chọn đăng xuất hoặc không thể hỏi lại.
   */
  requestReauthentication(): Promise<boolean> {
    if (this.reauthPromise) {
      return this.reauthPromise;
    }

    const user = this.currentUserSubject.value;
    if (!user?.username || this.router.url.startsWith('/login')) {
      return Promise.resolve(false);
    }

    this.stopTokenRefreshTimer();

    this.reauthPromise = new Promise<boolean>((resolve) => {
      this.reauthResolve = resolve;
      const data: ReloginDialogData = {
        username: user.username,
        submit: (password) => this.reauthenticate(user, password)
      };
      this.reauthModalRef = this.injector.get(NzModalService).create<ReloginDialogComponent, ReloginDialogData, boolean>({
        nzContent: ReloginDialogComponent,
        nzData: data,
        nzFooter: null,
        nzClosable: false,
        nzMaskClosable: false,
        nzKeyboard: false
      });
      this.reauthModalRef.afterClose.subscribe((result) => {
        this.reauthModalRef = undefined;
        this.finishReauthentication(result === true);
      });
    });

    return this.reauthPromise;
  }

  private finishReauthentication(success: boolean): void {
    const resolve = this.reauthResolve;
    if (!resolve) {
      return;
    }

    this.reauthResolve = undefined;
    this.reauthPromise = undefined;
    const modalRef = this.reauthModalRef;
    this.reauthModalRef = undefined;
    modalRef?.close(success);
    resolve(success);
  }

  /** Đăng nhập lại cho đúng người dùng hiện tại, không đổi sang tài khoản khác. */
  private async reauthenticate(user: UserProfile, password: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(
        `${AUTH_API_URL}/login?include_scoped=true`,
        { username: user.username, password } as LoginRequest
      )
    );

    const newUserId = decodeJwt(response.access_token)?.['user_id'];
    if (newUserId !== undefined && String(newUserId) !== String(user.id)) {
      throw new Error('Vui lòng đăng nhập đúng tài khoản đang sử dụng.');
    }

    this.setTokens(response.access_token, response.refresh_token, true);
    this.authChannel?.postMessage({
      type: 'tokens-refreshed',
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      rememberMe: true
    });
  }

  private isRefreshTokenRejected(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return [400, 401, 403].includes(error.status);
  }

  private endExpiredSession(): void {
    const returnUrl = this.router.url && !this.router.url.startsWith('/login')
      ? this.router.url
      : '/';

    // Phiên hết hạn cũng phải huỷ token web push của trình duyệt này, nếu không người đã bị đăng xuất vẫn
    // nhận push. Không còn JWT hợp lệ nên chỉ huỷ phía Firebase; không chờ để không chặn chuyển trang.
    void this.webPushService.huyDangKy(false);
    this.clearAuth();
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
  }

  logout(): Observable<void> {
    // Huỷ token web push trước khi xoá phiên (còn Bearer token để gọi backend); không chặn đăng xuất khi lỗi.
    return from(this.webPushService.huyDangKy()).pipe(switchMap(() => this.dangXuat()));
  }

  private dangXuat(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearAuth();
      return of(void 0);
    }

    return this.http.post<void>(
      `${AUTH_API_URL}/logout`,
      { refresh_token: refreshToken } as LogoutRequest
    ).pipe(
      tap(() => {
        this.clearAuth();
        this.router.navigate(['/login']);
      }),
      catchError(() => {
        // Even if logout fails, clear local auth
        this.clearAuth();
        this.router.navigate(['/login']);
        return of(void 0);
      })
    );
  }

  getCurrentUser(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${AUTH_API_URL}/me`, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
      })
    );
  }

  getScopedPermissions(): Observable<any> {
    return this.http.get<any>(`${AUTH_API_URL}/me/scoped`, {
      headers: this.getAuthHeaders()
    });
  }

  changePassword(data: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(
      `${AUTH_API_URL}/me/change-password`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  requestPasswordReset(data: ForgotPasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${AUTH_API_URL}/forgot-password`, data);
  }

  validateResetPasswordToken(token: string): Observable<ResetPasswordValidationResponse> {
    return this.http.get<ResetPasswordValidationResponse>(`${AUTH_API_URL}/reset-password/validate`, {
      params: { token }
    });
  }

  resetPassword(data: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${AUTH_API_URL}/reset-password`, data);
  }

  verifyToken(data: VerifyTokenRequest): Observable<VerifyTokenResponse> {
    return this.http.post<VerifyTokenResponse>(
      `${AUTH_API_URL}/verify`,
      data
    );
  }

  introspectToken(data: VerifyTokenRequest): Observable<any> {
    return this.http.post<any>(
      `${AUTH_API_URL}/introspect`,
      data
    );
  }
}

